import React, { useState, useEffect, useRef } from "react";
import ReactApexChart from "react-apexcharts";
import { useSelector } from "react-redux";
import { Select } from "antd";
import { ReactComponent as DownArrow } from "../../Assets/Icons/greyDownArrow.svg";

const VoucherRedemptionRate = () => {
  const { redemptionRates } = useSelector(
    (state) => state?.merchantReducer
  );
  const [selectedFilter, setSelectedFilter] = useState("Last Year");
  const [filteredSeries, setFilteredSeries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [renderChart, setRenderChart] = useState(true);
  // Create a ref to the chart container for scoped DOM operations
  const chartContainerRef = useRef(null);

  const baseChartOptions = {
    legend: {
      show: true,
      position: "top",
      horizontalAlign: "left",
    },
    colors: [
      "#1E90FF",
      "#FF6347",
      "#32CD32",
      "#FFD700",
      "#8A2BE2",
      "#FF4500",
      "#40E0D0",
      "#FF1493",
      "#7FFF00",
      "#4682B4",
    ],
    chart: {
      fontFamily: "Poppins, sans-serif",
      height: 335,
      type: "area",
      toolbar: {
        show: false,
      },
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      curve: "smooth",
      width: 2,
    },
    fill: {
      type: "none",
      opacity: 0,
    },
    xaxis: {
      type: "category",
      categories: [],
      title: {
        text: `Time Period (Last Year)`,
        style: {
          fontSize: "14px",
          fontWeight: "400",
          color: "#373d3f",
          fontFamily: "Poppins, sans-serif",
        },
      },
    },
    yaxis: {
      min: 0,
      max: 100,
      labels: {
        formatter: (value) => `${value}%`,
      },
      title: {
        text: "Redemption Rate",
        style: {
          fontSize: "14px",
          fontWeight: "400",
          color: "#373d3f",
          fontFamily: "Poppins, sans-serif",
        },
      },
    },
    tooltip: {
      shared: true,
      intersect: false,
    },
  };

  // Scoped chart rerender that only affects this component
  const forceChartRerender = () => {
    // First, destroy the chart by removing it from DOM
    setRenderChart(false);

    // Only remove ApexCharts elements within this component
    if (chartContainerRef.current) {
      const chartElements = chartContainerRef.current.querySelectorAll(".apexcharts-canvas");
      chartElements.forEach((element) => {
        if (element.parentNode) {
          element.parentNode.removeChild(element);
        }
      });
    }

    // Give time for cleanup then re-render
    setTimeout(() => {
      setRenderChart(true);
    }, 50);
  };

  const processRedemptionData = (redemptionData) => {
    if (!redemptionData || !redemptionData.success || !redemptionData.data?.redemptionRate) {
      setFilteredSeries([]);
      setCategories([]);
      setLoading(false);
      return;
    }

    let { redemptionRate } = redemptionData.data;

    // Handle empty data
    if (
      !redemptionRate ||
      redemptionRate.length === 0 ||
      !redemptionRate[0]?.redemptionRates?.length
    ) {
      setFilteredSeries([]);
      setCategories([]);
      setLoading(false);
      return;
    }

    // Calculate total redemption rate for each offer and sort
    redemptionRate = redemptionRate.map((offer) => ({
      ...offer,
      totalRedemption: offer.redemptionRates.reduce((sum, rate) => sum + parseFloat(rate.rate || 0), 0),
    }));
    redemptionRate.sort((a, b) => b.totalRedemption - a.totalRedemption);
    // Take top 5 offers
    const topOffers = redemptionRate.slice(0, 5);

    // Collect all unique periods from top offers
    const uniquePeriods = new Set();
    topOffers.forEach((offer) => {
      offer.redemptionRates.forEach((rate) => {
        const periodParts = rate.period.split("-");
        if (periodParts.length === 2) {
          const monthName = new Date(`${rate.period}-01`)
            .toLocaleString("default", { month: "short" })
            .toUpperCase();
          uniquePeriods.add(`${monthName} ${periodParts[0]}`);
        } else if (periodParts.length === 3) {
          const day = periodParts[2].padStart(2, "0");
          const monthName = new Date(
            `${periodParts[0]}-${periodParts[1]}-01`
          )
            .toLocaleString("default", { month: "short" })
            .toUpperCase();
          uniquePeriods.add(`${day} ${monthName}`);
        }
      });
    });

    // Convert unique periods to a sorted array
    let sortedPeriods = Array.from(uniquePeriods).sort((a, b) => {
      const parseDate = (period) => {
        const parts = period.split(" ");
        if (parts.length === 2) {
          return new Date(`${parts[1]} ${parts[0]} 01`);
        } else if (parts.length === 3) {
          return new Date(`${new Date().getFullYear()} ${parts[1]} ${parts[0]}`);
        }
        return new Date(period);
      };
      return parseDate(a) - parseDate(b);
    });
    // Take the most recent 6 periods
    sortedPeriods = sortedPeriods.slice(-6);

    // Prepare series data for top offers and limited periods
    const series = topOffers.map((offer) => {
      const data = sortedPeriods.map((period) => {
        const rateForPeriod = offer.redemptionRates.find((rate) => {
          const periodParts = rate.period.split("-");
          if (periodParts.length === 2) {
            const monthName = new Date(`${rate.period}-01`)
              .toLocaleString("default", { month: "short" })
              .toUpperCase();
            return `${monthName} ${periodParts[0]}` === period;
          } else if (periodParts.length === 3) {
            const day = periodParts[2].padStart(2, "0");
            const monthName = new Date(
              `${periodParts[0]}-${periodParts[1]}-01`
            )
              .toLocaleString("default", { month: "short" })
              .toUpperCase();
            return `${day} ${monthName}` === period;
          }
          return false;
        });
        return rateForPeriod ? parseFloat(rateForPeriod.rate) : 0;
      });
      return {
        name: offer.offerName,
        data,
      };
    });

    setCategories(sortedPeriods);
    setFilteredSeries(series);
    setLoading(false);
  };

  const handleFilterChange = (filterValue) => {
    console.log("filterValue", filterValue);
    setSelectedFilter(filterValue);
    // Note: The actual API call should be handled by the parent component
    // This component now only processes the data from Redux state
  };

  useEffect(() => {
    if (redemptionRates) {
      processRedemptionData(redemptionRates);
    }
  }, [redemptionRates]);

  useEffect(() => {
    setSelectedFilter("Last Year");
    // Cleanup on unmount - scoped to only this component
    return () => {
      if (chartContainerRef.current) {
        const chartElements = chartContainerRef.current.querySelectorAll(".apexcharts-canvas");
        chartElements.forEach((element) => {
          if (element.parentNode) {
            element.parentNode.removeChild(element);
          }
        });
      }
    };
  }, []);

  const filterOptions = [
    { label: "Last Week", value: "Last Week" },
    { label: "Last Month", value: "Last Month" },
    { label: "Last Year", value: "Last Year" },
  ];

  const isLastWeek = selectedFilter === "Last Week";

  // Generate a new chart options object each time to avoid reference issues
  const getChartOptions = () => {
    return {
      ...baseChartOptions,
      xaxis: {
        ...baseChartOptions.xaxis,
        categories: categories,
        title: {
          ...baseChartOptions.xaxis.title,
          text: `Time Period (${selectedFilter})`,
        },
      },
    };
  };

  return (
    <div className="rounded-sm border border-stroke bg-white px-5 shadow-default dark:border-strokedark dark:bg-boxdark sm:px-7.5 summary offersChart">
      <div className="flex flex-col gap-4 xl:flex-row xl:gap-0 justify-between w-full mt-4">
        <span className="text-[20px] font-poppins font-[600] text-[black] whitespace-nowrap">
          Offer Redemption Rate
        </span>
        <Select
          value={selectedFilter}
          className={
            "border border-[#00ADEF] rounded-md pr-3 text-lg font-medium outline-none h-[40px]"
          }
          suffixIcon={<DownArrow />}
          options={filterOptions}
          defaultValue={selectedFilter}
          onChange={handleFilterChange}
          dropdownMatchSelectWidth={false}
          style={{ minWidth: "100px", maxWidth: "200px" }}
        />
      </div>

      <div
        ref={chartContainerRef}  
        className={`w-full flex items-center ${
          filteredSeries.length > 0 && filteredSeries[0]?.data?.length > 0 && !loading
            ? isLastWeek ? "justify-center" : "overflow-x-auto justify-start"
            : "justify-center"
        }`}
      >
        <div id="chartOne" className={`mt-4 ${isLastWeek ? "w-full" : "min-w-[800px] w-full"} h-[430px]`}>
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center text-gray-500 text-lg font-medium">
                Loading...
              </div>
            </div>
          ) : filteredSeries.length > 0 &&
            filteredSeries[0]?.data?.length > 0 &&
            renderChart ? (
            <ReactApexChart
              key={`chart-${selectedFilter}-${Date.now()}`}
              options={getChartOptions()}
              series={JSON.parse(JSON.stringify(filteredSeries))} // Deep clone to avoid reference issues
              type="area"
              height={410}
            />
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center text-gray-500 text-lg font-medium">
                {!renderChart
                  ? "Updating Chart..."
                  : "No Redemption Data Found"}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VoucherRedemptionRate;